import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeTokens } from './theme.model';
import { ALL_THEMES, DEFAULT_THEME } from './themes.data';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private readonly renderer: Renderer2;
  private readonly _theme$ = new BehaviorSubject<ThemeTokens>(DEFAULT_THEME);

  /** Observable que emite cada vez que cambia el tema. */
  readonly theme$ = this._theme$.asObservable();

  /** Tema activo en este momento. */
  get current(): ThemeTokens { return this._theme$.value; }

  /** Lista de todos los temas disponibles. */
  readonly all = ALL_THEMES;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    // Siempre usar el tema por defecto (slate) — ignorar localStorage
    this.apply(DEFAULT_THEME);
  }

  setTheme(idOrTokens: string | ThemeTokens): void {
    const theme = typeof idOrTokens === 'string'
      ? (ALL_THEMES.find(t => t.id === idOrTokens) ?? DEFAULT_THEME)
      : idOrTokens;
    this.apply(theme);
  }

  // ── Aplicar tema: CSS vars + clase dark/light en <html> ─────────────────
  private apply(theme: ThemeTokens): void {
    const root = document.documentElement;

    // Clase base para poder usar selectores CSS globales si hace falta
    this.renderer.removeClass(root, 'theme-dark');
    this.renderer.removeClass(root, 'theme-light');
    this.renderer.addClass(root, theme.dark ? 'theme-dark' : 'theme-light');
    root.setAttribute('data-theme', theme.id);

    // Mapear cada token a una CSS variable
    const vars: Record<string, string> = {
      '--cc-page-bg':         theme.pageBg,
      '--cc-card-bg':         theme.cardBg,
      '--cc-card-bg-2':       theme.cardBg2,
      '--cc-card-bg-3':       theme.cardBg3,
      '--cc-card-border':     theme.cardBorder,
      '--cc-card-shadow':     theme.cardShadow,

      '--cc-text-primary':    theme.textPrimary,
      '--cc-text-secondary':  theme.textSecondary,
      '--cc-text-muted':      theme.textMuted,

      '--cc-accent':          theme.accent,
      '--cc-accent-glow':     theme.accentGlow,
      '--cc-accent-soft':     theme.accentSoft,

      '--cc-success':         theme.success,
      '--cc-warning':         theme.warning,
      '--cc-danger':          theme.danger,

      '--cc-divider':         theme.divider,

      '--cc-chart-grid':         theme.chartGrid,
      '--cc-chart-axis':         theme.chartAxis,
      '--cc-chart-tooltip-bg':   theme.chartTooltipBg,
      '--cc-chart-tooltip-text': theme.chartTooltipText,

      // Paleta de series
      '--cc-p1': theme.palette[0],
      '--cc-p2': theme.palette[1],
      '--cc-p3': theme.palette[2],
      '--cc-p4': theme.palette[3],
      '--cc-p5': theme.palette[4],
      '--cc-p6': theme.palette[5],
      '--cc-p7': theme.palette[6],
      '--cc-p8': theme.palette[7],
    };

    Object.entries(vars).forEach(([prop, val]) =>
      root.style.setProperty(prop, val),
    );

    localStorage.setItem('cc-theme', theme.id);
    this._theme$.next(theme);
  }
}
